import * as React from "react";

import { DateLike } from "~/lib/date";
import LegacyDispatcher from "~/lib/legacyDispatcher";

import JobLogGroupStore from "~/stores/JobLogGroupStore";
import JobLogLineStore from "~/stores/JobLogLineStore";
import JobLogStore from "~/stores/JobLogStore";
import {
  LogGroupHeader,
  LogGroupName,
  LogGroupTime,
  LogLineNumber,
  LogTimestamp,
} from "~/components/shared/Log";
import { LogLineActionsDropdown } from "~/components/shared/LogLineActionsDropdown";
import { useLogLineSelection } from "~/components/build/Show/lib/useLogLineSelection";

const getStateFromStores = (id: string, jobId: string) => {
  return {
    highlighted: JobLogLineStore.isHighlighted(id),
    expanded: JobLogGroupStore.isExpanded(id),
    highlightedRange: JobLogLineStore.getHighlightedRange(jobId),
  };
};

interface LogLineSelectionProps {
  selectLine: (jobId: string, lineNumber: number) => void;
  selectRange: (jobId: string, startLine: number, endLine: number) => void;
}

interface Props extends React.HTMLAttributes<HTMLTableRowElement> {
  id: string;
  jobId: string;
  name: string;
  number: number;
  deemphasized?: boolean;
  startedAt: DateLike | null | undefined;
  finishedAt: DateLike | null | undefined;
  finished: boolean;
  durationPercentage: number | null | undefined;
  timestampsOn: boolean | null | undefined;
  timestampsInUTC: boolean | null | undefined;
  textContent?: string;
  onTimestampClick: (arg1: React.MouseEvent<HTMLElement>) => void | null | undefined;
  innerRef?: React.Ref<HTMLTableRowElement> | null;
}

type PropsWithSelection = Props & LogLineSelectionProps;

type State = {
  highlighted: boolean;
  expanded: boolean;
  highlightedRange: { start: number; end: number } | null;
};

class JobLogOutputHeader extends React.PureComponent<PropsWithSelection, State> {
  constructor(props: PropsWithSelection) {
    super(props);

    this.state = {
      ...getStateFromStores(props.id, props.jobId),
    };
  }

  componentDidMount() {
    JobLogLineStore.addChangeListener(this.props.id, this.handleStoreChange);
    JobLogGroupStore.addChangeListener(this.props.id, this.handleStoreChange);

    // Listen for range changes to update highlighting when range selection changes
    JobLogLineStore.addRangeChangeListener(this.props.jobId, this.handleStoreChange);
  }

  componentWillUnmount() {
    JobLogLineStore.removeChangeListener(this.props.id, this.handleStoreChange);
    JobLogGroupStore.removeChangeListener(this.props.id, this.handleStoreChange);
    JobLogLineStore.removeRangeChangeListener(this.props.jobId, this.handleStoreChange);
  }

  handleStoreChange = () => {
    this.setState(getStateFromStores(this.props.id, this.props.jobId));
  };

  handleToggleHeaderClick = (evt: React.MouseEvent<HTMLTableCellElement>) => {
    evt.preventDefault();

    // @ts-expect-error - TS2554 - Expected 6 arguments, but got 2.
    return LegacyDispatcher.emit("job_log_group:toggle", {
      groupId: this.props.id,
    });
  };

  handleNumberClick = (evt: React.MouseEvent<HTMLTableCellElement>) => {
    evt.preventDefault();

    const { jobId, selectLine, selectRange } = this.props;
    const lineNumber = this.props.number;

    // Handle shift+click for range selection
    if (evt.shiftKey) {
      const anchor = JobLogLineStore.getAnchorLine();

      if (anchor && anchor.jobId === jobId) {
        // Create range from anchor to this line
        const startLine = Math.min(anchor.lineNumber, lineNumber);
        const endLine = Math.max(anchor.lineNumber, lineNumber);

        // Update URL - this will trigger the provider to sync to stores
        selectRange(jobId, startLine, endLine);
        return;
      }
    }

    // Regular click: select single line
    // Update URL - this will trigger the provider to sync to stores
    selectLine(jobId, lineNumber);
  };

  handleTimestampClick = (evt: React.MouseEvent<HTMLElement>) => {
    evt.preventDefault();

    if (this.props.onTimestampClick) {
      this.props.onTimestampClick(evt);
    }
  };

  render() {
    const {
      deemphasized,
      timestampsInUTC,
      durationPercentage,
      finished,
      finishedAt,
      id,
      jobId,
      name,
      number,
      onTimestampClick,
      startedAt,
      timestampsOn,
      innerRef,
      textContent: _textContent,
      selectLine: _selectLine,
      selectRange: _selectRange,
      className,
      ...props
    } = this.props;

    const actions = (
      <LogLineActionsDropdown
        jobId={this.props.jobId}
        lineNumber={this.props.number}
        groupEndLine={JobLogStore.getGroupEndLine(this.props.jobId, this.props.number)}
      />
    );

    return (
      <LogGroupHeader
        ref={innerRef}
        number={this.props.number}
        highlighted={this.state.highlighted}
        deemphasized={this.props.deemphasized}
        className={className}
        {...props}
      >
        <LogLineNumber
          onClick={this.handleNumberClick}
          number={this.props.number}
          actions={actions}
          jobId={this.props.jobId}
        />

        {this.props.timestampsOn && (
          <LogTimestamp
            time={this.props.startedAt}
            utc={this.props.timestampsInUTC}
            onClick={this.handleTimestampClick}
          />
        )}

        <LogGroupName
          onClick={this.handleToggleHeaderClick}
          name={this.props.name}
          expanded={this.state.expanded}
        />

        <LogGroupTime
          onClick={this.handleToggleHeaderClick}
          startedAt={this.props.startedAt}
          finishedAt={this.props.finishedAt}
          finished={this.props.finished}
          durationPercentage={this.props.durationPercentage}
        />
      </LogGroupHeader>
    );
  }
}

/**
 * Wrapper component that provides the log line selection context to the class component.
 */
export default function JobLogOutputHeaderWithSelection(props: Props) {
  const { selectLine, selectRange } = useLogLineSelection();

  return <JobLogOutputHeader {...props} selectLine={selectLine} selectRange={selectRange} />;
}
